import * as React from 'react'
import type { PlantLineSummary } from '@axilon-apps/domains-ontology-ui-state'
import { FlowTile } from './FlowTile'
import { computeFlowDagLayout, FLOW_DAG_NODE_H, FLOW_DAG_NODE_W } from './flow-dag-layout'

/**
 * Connected DAG rendering of a line's steps along their `FEEDS` edges,
 * used by `SublineFlow` when the flow branches or merges (so a single
 * caret-separated row would misrepresent the topology). Layout comes
 * from dagre via `computeFlowDagLayout`; tiles are the same clickable
 * `FlowTile` pills as the linear view, and edges are plain SVG beziers,
 * so the section scrolls like any other content instead of becoming a
 * pan/zoom canvas.
 *
 * Orientation adapts to the available width: the flow lays out
 * left-to-right, but when that would run off the page the diagram
 * flips top-to-bottom and grows taller instead — vertical space is
 * free, the page already scrolls that way.
 */
export interface SublineFlowDagProps {
  siteId: string
  sublines: PlantLineSummary[]
  /** Directed FEEDS edges; endpoints outside `sublines` are ignored. */
  feeds: Array<{ source: string; target: string }>
}

export function SublineFlowDag({
  siteId,
  sublines,
  feeds
}: SublineFlowDagProps): React.ReactElement {
  const markerId = React.useId()
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [containerW, setContainerW] = React.useState(0)

  React.useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = (): void => setContainerW(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const layout = React.useMemo(() => {
    const ids = sublines.map((s) => s.id)
    const horizontal = computeFlowDagLayout(ids, feeds, 'LR')
    // Too wide for the section (0 = unmeasured, e.g. first paint): grow
    // taller instead of running off the page.
    if (containerW > 0 && horizontal.width > containerW) {
      return computeFlowDagLayout(ids, feeds, 'TB')
    }
    return horizontal
  }, [containerW, feeds, sublines])

  const byId = React.useMemo(() => new Map(sublines.map((s) => [s.id, s])), [sublines])

  return (
    <div ref={containerRef}>
      <div
        data-testid="subline-flow-dag"
        className="relative"
        style={{ width: layout.width, height: layout.height }}
      >
        <svg
          className="absolute inset-0 text-ink-4"
          width={layout.width}
          height={layout.height}
          aria-hidden
        >
          <defs>
            <marker
              id={markerId}
              viewBox="0 0 8 8"
              refX="7"
              refY="4"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M0,0 L8,4 L0,8 z" fill="currentColor" />
            </marker>
          </defs>
          {layout.edges.map((edge) => (
            <path
              key={edge.key}
              d={edge.path}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.25}
              markerEnd={`url(#${markerId})`}
            />
          ))}
        </svg>
        {layout.nodes.map(({ id, x, y }) => {
          const subline = byId.get(id)
          if (!subline) return null
          return (
            <div
              key={id}
              className="absolute overflow-hidden [&>a]:h-full [&>a]:w-full"
              style={{ left: x, top: y, width: FLOW_DAG_NODE_W, height: FLOW_DAG_NODE_H }}
            >
              <FlowTile
                kind="line"
                id={subline.id}
                label="Line"
                node={{ ...subline, siteId }}
                name={subline.name}
                nameLayout="wrap"
                subtitle={`${subline.equipmentCount} ${subline.equipmentCount === 1 ? 'piece' : 'pieces'} of equipment`}
                memberIds={subline.equipmentIds}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
